import { ReactElement, ReactNode } from 'react';
import { Chip, chipClasses, ChipProps, styled } from '@mui/material';
import classNames from 'classnames';

import { isIconOption } from './Icon/iconOptions';
import { Icon, IconOption, IconProps } from './Icon';

export type TagProps = Omit<ChipProps, 'deleteIcon' | 'icon'> & {
  icon?: IconOption | ReactNode;
  soft?: boolean;
};

type DeleteIconProps = IconProps & {
  soft?: boolean;
};

const DeleteIcon = styled(({ className, soft = false, ...rest }: DeleteIconProps) => (
  <Icon className={classNames(className, { soft })} {...rest} />
))`
  &.${chipClasses.deleteIcon} {
    font-size: 16px;
    color: inherit;
    opacity: 0.7;
    margin: 0;

    &:hover {
      opacity: 0.6;
    }

    &:not(.soft):hover {
      color: inherit;
      opacity: 1;
    }
  }
`;

export const Tag = styled(({ className, icon, soft = false, size = 'tiny', ...rest }: TagProps) => (
  <Chip
    className={classNames(className, { soft, 'has-icon': icon })}
    deleteIcon={<DeleteIcon name='close' />}
    icon={icon ? isIconOption(icon) ? <Icon name={icon} /> : (icon as ReactElement) : undefined}
    size={size}
    {...rest}
  />
))`
  display: flex;
  align-items: center;
  border-radius: 2px;

  ${({ color, theme, variant }) => {
    if (!color && (!variant || variant === 'filled')) {
      return `
        &:focus-visible {
            background: ${theme.palette.action.focus};
          }

          &:not(.soft) {
            background: ${theme.palette.text.primary};
            color: ${theme.palette.getContrastText(theme.palette.text.primary)};

            &:focus-visible {
              background: ${theme.palette.text.secondary};
            }
          }
      `;
    }
  }}

  .${chipClasses.icon} {
    color: inherit !important;
    font-size: inherit !important;
  }

  .${chipClasses.label} {
    padding: ${({ theme }) => theme.spacing(0, 0.5)};
  }

  &.has-icon .${chipClasses.label} {
    padding-left: ${({ theme }) => theme.spacing()};
  }
`;
